<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Asset Maintenance Records
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2 justify-items-between">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="4" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                          >CSV</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                          >EXCEL</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="4" cols="12">
                    <div class="text-right pr-2">
                      <v-btn
                        v-if="$insertAccess($route.fullPath)"
                        @click="goToAdd()"
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi mdi-plus"
                        rounded="xl"
                      >
                        Add New Maintenance
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
                <v-data-table
                  :headers="headers"
                  :items="getSchema"
                  :loading="loading"
                  v-model:items-per-page="itemsPerPage"
                  :search="search"
                  :hover="true"
                  density="compact"
                >

                  <template v-slot:item.maintenance_date="{ item }">
                    {{ formatDate(item.maintenance_date) }}
                  </template>

                  <template v-slot:item.location="{ item }">
                    <v-chip
                      color="blue"
                      size="small"
                      variant="tonal"
                      prepend-icon="mdi-map-marker"
                    >
                      {{ getLocationName(item) }}
                    </v-chip>
                  </template>

                  <template v-slot:item.supplier="{ item }">
                    <span v-if="item.supplier">{{ item.supplier.supplier_name }}</span>
                    <span v-else class="text-grey">N/A</span>
                  </template>

                  <template v-slot:item.total_cost="{ item }">
                    <span class="font-weight-bold text-success">{{ formatCurrency(item.total_cost) }}</span>
                  </template>

                  <template v-slot:item.details="{ item }">
                    <v-chip
                      color="orange"
                      size="small"
                      variant="tonal"
                      prepend-icon="mdi-wrench"
                      >{{ item.details?.length || 0 }} Asset(s)</v-chip
                    >
                  </template>

                  <template v-slot:item.remarks="{ item }">
                    <span class="text-body-2">{{ item.remarks || "N/A" }}</span>
                  </template>

                  <template v-slot:item.approval_status_display="{ item }">
                    <v-chip
                    v-if="item.approval_current_process && item.approval_current_process.status === 'Pending'"
                    color="yellow-darken-3"
                    size="small"
                    >
                        Wating for {{ item.approval_current_process.approval_step_name.name }}
                    </v-chip>

                    <v-chip
                    v-else-if="item.approval_current_process && item.approval_current_process.status === 'Approved'"
                    color="green"
                    size="small"
                    >
                        Approved By {{ item.approval_current_process.approval_step_name.name }}
                    </v-chip>
                    
                    <v-chip
                    v-else-if="item.approval_current_process && item.approval_current_process.status === 'Approved'"
                    color="red"
                    size="small"
                    >
                        Cancelled By  {{ item.approval_current_process.approval_step_name.name }}
                    </v-chip>
                    <v-chip
                    v-else
                    color="green"
                    size="small"
                    >
                    {{ item.approval_status }}
                    </v-chip>
                  </template>

                  <template v-slot:item.status="{ item }">
                    <v-chip
                      :color="item.status === 0 ? 'grey' : 'green'"
                      size="small"
                      variant="flat"
                    >
                      {{ formatStatus(item.status) }}
                    </v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <div class="d-flex align-center justify-center" style="gap: 6px;">
                      <v-btn
                        icon="mdi-pencil"
                        size="small"
                        variant="text"
                        color="orange"
                        @click="editItem(item)"
                        v-if="$updateAccess($route.fullPath) && canEdit(item)"
                      />
                      <v-btn
                        icon="mdi-file-document-outline"
                        size="small"
                        variant="text"
                        color="primary"
                        @click="viewItem(item)"
                        v-if="$viewAccess($route.fullPath)"
                      />
                    </div>
                  </template>
                  
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { ref } from "vue";
import { toast } from "vue3-toastify";

export default {
  components: {},
  data: () => ({
    itemsPerPage: "10",
    panel: [0],
    search: "",
    headers: [
      { title: "#SL", align: "center", key: "id", width: "80px", sortable: false },
      { title: "Code", key: "code", align: "center", sortable: false, width: "120px" },
      { title: "Maintenance Date", key: "maintenance_date", align: "center", sortable: false, width: "150px" },
      { title: "Location", key: "location", align: "center", sortable: false, width: "150px" },
      { title: "Supplier", key: "supplier", align: "center", sortable: false, width: "150px" },
      { title: "Reference No", key: "reference_no", align: "center", sortable: false, width: "130px" },
      { title: "Total Cost", key: "total_cost", align: "center", sortable: false, width: "120px" },
      { title: "Assets", key: "details", align: "center", sortable: false, width: "100px" },
      { title: "Remarks", key: "remarks", align: "center", sortable: false, width: "200px" },
      { title: "Approval Status", align: "center", key: "approval_status_display", sortable: true },
      { title: "Status", key: "status", align: "center", sortable: false, width: "120px" },
      { title: "Actions", align: "center", key: "actions", sortable: false, width: "120px" },
    ],
  }),
  watch: {},
  created() {
    this.loadLocations();
    this.loadAssetMaintenances();
  },
  computed: {
    getSchema() {
      return this.$store.getters.getAssetMaintenanceList;
    },
    getAssetLocationsList() {
      let data = this.$store.getters.getAssetLocationsList;
      if (data.length > 0) {
        return data.filter((item) => item.status == 1);
      }
      return [];
    },
  },
  setup() {
    const errorMessages = ref("");
    const loading = ref(false);
    const store = useStore();

    return {
      errorMessages,
      loading,
    };
  },
  methods: {
    loadLocations() {
      if (this.$store.getters.getAssetLocationsList.length == 0) {
        this.$store.dispatch("getAssetLocationsList");
      }
    },
    loadAssetMaintenances() {
      this.loading = true;
      this.$store
        .dispatch("getAssetMaintenanceList", {})
        .then((response) => {
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          toast.error(error.response?.data?.message || "Failed to load asset maintenances");
        });
    },
    goToAdd() {
      this.$store.commit("UNSET_EDITING_ASSET_MAINTENANCE");
      this.$router.push({ name: "add-maintenance" });
    },
    editItem(item) {
      this.$store.commit("SET_EDITING_ASSET_MAINTENANCE", item);
      this.$router.push({ name: "add-maintenance" });
    },
    canEdit(item){
      return (!item.approval_status || item.approval_status.toLowerCase() !== 'approved' || item.status == 0)
    },
    viewItem(item) {
      const raw = item && item.raw ? item.raw : item;
      const id = raw?.id;
      if (!id) return;
      this.$router.push({ name: "maintenance-record-details", params: { id } });
    },
    formatDate(date) {
      if (!date) return "";
      try {
        const d = new Date(date);
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
      } catch {
        return "";
      }
    },
    formatCurrency(amount) {
      if (!amount) return "0.00";
      return parseFloat(amount).toFixed(2);
    },
    getLocationName(item) {
      if (item.asset_location_id) {
        const location = this.getAssetLocationsList.find(loc => loc.id === item.asset_location_id);
        return location ? location.name : "N/A";
      }
      return "N/A";
    },
    exportToPDF() {
      let serialNumber = 1;
      const columns = ["#SL", "Code", "Maintenance Date", "Location", "Supplier", "Total Cost", "Assets", "Status"];
      const rows = this.getSchema.map((item) => [
        serialNumber++,
        item.code,
        this.formatDate(item.maintenance_date),
        this.getLocationName(item),
        item.supplier?.supplier_name || "N/A",
        this.formatCurrency(item.total_cost),
        item.details?.length || 0,
        this.formatStatus(item.status),
      ]);
      this.$exportTableToPDF(columns, rows, "Asset Maintenance List", "asset_maintenance_list_" + this.$fileDate());
    },
    exportToExcel() {
      var data = [["#SL", "Code", "Maintenance Date", "Location", "Supplier", "Total Cost", "Assets", "Status"]];
      var serialNumber = 1;
      this.getSchema.forEach((item) => {
        data.push([
          serialNumber++,
          item.code,
          this.formatDate(item.maintenance_date),
          this.getLocationName(item),
          item.supplier?.supplier_name || "N/A",
          this.formatCurrency(item.total_cost),
          item.details?.length || 0,
          this.formatStatus(item.status),
        ]);
      });
      this.$exportTableToExcel(data, "asset_maintenance_list_" + this.$fileDate());
    },
    exportToCSV() {
      var data = [["#SL", "Code", "Maintenance Date", "Location", "Supplier", "Total Cost", "Assets", "Status"]];
      var serialNumber = 1;
      this.getSchema.forEach((item) => {
        data.push([
          serialNumber++,
          item.code,
          this.formatDate(item.maintenance_date),
          this.getLocationName(item),
          item.supplier?.supplier_name || "N/A",
          this.formatCurrency(item.total_cost),
          item.details?.length || 0,
          this.formatStatus(item.status),
        ]);
      });
      this.$exportTableToCSV(data, "asset_maintenance_list_" + this.$fileDate());
    },
    formatStatus(status) {
      if (status === 0) return "Draft";
      if (status === 1) return "Submitted";
      return "Unknown";
    },
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}

:deep(.v-data-table__td) {
  text-align: center;
}
</style>

